import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useModalA11y } from '@/hooks/useModalA11y'
import { Backdrop } from './Backdrop'
import { Button } from './Button'

export interface BottomSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  /** Accessible label when no visible title is provided. */
  ariaLabel?: string
  /**
   * EP-114 (new design system, D-0b) — opt-in Figma-spec grab handle: a
   * 40×5px `bg-fd-border` pill (vs the default 40×4px `bg-base-300`) and a
   * `rounded-t-[24px]` panel corner (vs the default `rounded-t-2xl`, 16px),
   * per `docs/ep-114-onboarding-ui-update.md` §5.4 (`183:3002`/`183:3329`)
   * and §6 (G-2). Defaults to `false` so every existing caller renders
   * byte-identical to before — strictly additive, no production caller
   * passes this yet (forward-looking kit infra).
   */
  showHandle?: boolean
}

/**
 * Mobile bottom sheet — slides up from the bottom of the viewport with a
 * dimmed backdrop. Used on mobile where a full modal or the OS-native select
 * picker is undesirable (e.g. the in-app Select options list).
 *
 * Z-ladder: sits at z-[70] — above the QuickActionBar (z-[60]) and below the
 * CommandPalette (z-[100]). Backdrop carries pointer-events-auto so taps don't
 * fall through to the page; Escape and backdrop-click both close. Body scroll
 * is locked while open. Returns null when closed (no DOM cost).
 */
export function BottomSheet({ isOpen, onClose, title, children, ariaLabel, showHandle = false }: BottomSheetProps) {
  const { t: tc } = useTranslation('common')

  // Escape close + body scroll lock. No focus trap (bottom sheet is a
  // supplemental picker, not a blocking dialog).
  useModalA11y({ open: isOpen, onClose, escapeToClose: true, scrollLock: true, focusTrap: false })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={ariaLabel ?? title}>
      {/* Backdrop — pointer-events-auto blocks click-through; tap closes. */}
      <Backdrop onClick={onClose} />
      {/* Panel. The height cap is also bounded by the container (100%):
          inside a Modal the dialog's box carries a transform, which makes it
          the containing block for this fixed layer, so the sheet is only as
          tall as the modal. Capped at 80vh alone the panel outgrew it and
          its title and search box were cut off above the modal's top edge.
          Outside a modal the container is the viewport and 80vh still wins. */}
      <div
        data-testid="bottom-sheet-panel"
        className={`absolute bottom-0 inset-x-0 ${showHandle ? 'rounded-t-[24px]' : 'rounded-t-2xl'} bg-base-100 shadow-lg max-h-[min(80vh,100%)] overflow-y-auto pb-[max(env(safe-area-inset-bottom),1rem)] animate-slide-up`}
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-3 pb-1" aria-hidden="true">
          <div
            data-testid="bottom-sheet-handle"
            className={showHandle ? 'h-[5px] w-10 rounded-full bg-fd-border' : 'h-1 w-10 rounded-full bg-base-300'}
          />
        </div>
        {title && (
          <div className="flex items-center justify-between px-4 py-2">
            <h3 className="font-semibold text-base">{title}</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              shape="circle"
              onClick={onClose}
              aria-label={tc('actions.close')}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
        <div className="px-2 pb-2">{children}</div>
      </div>
    </div>
  )
}
